import { ref, watch, computed } from '@vue/composition-api'
import store from '@/store'
import i18n from '@/libs/i18n'
import { title } from '@core/utils/filter'

// Notification
import { useToast } from 'vue-toastification/composition'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'

export default function useGroupList() {
  // Use toast
  const toast = useToast()

  const refGroupListTable = ref(null)

  // Table Handlers
  const tableColumns = [
    { key: 'name', sortable: false },
    { key: 'customerName', sortable: false },
    { key: 'assetsCount', sortable: false },
    { key: 'actions' },
  ]

  const perPage = ref(10)
  const totalGroup = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const searchQuery = ref('')
  const customerId = ref('')
  const searchCustomer = ref('')
  const includeSubcustomers = ref(true)
  const sortBy = ref('id')
  const isSortDirDesc = ref(true)
  const isTableBusy = ref(false)

  const dataMeta = computed(() => {
    const localItemsCount = refGroupListTable.value ? refGroupListTable.value.localItems.length : 0
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalGroup.value,
    }
  })

  const hasActiveFilters = computed(() => {
    return Boolean(searchQuery.value || customerId.value || includeSubcustomers.value === false)
  })

  const activeFiltersCount = computed(() => {
    let count = 0
    if (searchQuery.value) count++
    if (customerId.value) count++
    if (includeSubcustomers.value === false) count++
    return count
  })

  const hasAdvancedFilters = computed(() => {
    return Boolean(customerId.value || includeSubcustomers.value === false)
  })

  const activeAdvancedFiltersCount = computed(() => {
    let count = 0
    if (customerId.value) count++
    if (includeSubcustomers.value === false) count++
    return count
  })

  const clearAllFilters = () => {
    searchQuery.value = ''
    customerId.value = ''
    searchCustomer.value = ''
    includeSubcustomers.value = true
    currentPage.value = 1
  }

  const refetchData = () => {
    if (refGroupListTable.value) {
      refGroupListTable.value.refresh()
    }
  }

  watch([searchQuery, customerId, includeSubcustomers], () => {
    if (currentPage.value !== 1) {
      currentPage.value = 1
    } else {
      refetchData()
    }
  })

  watch([currentPage, perPage], () => {
    refetchData()
  })

  const fetchAllGroup = (ctx, callback) => {
    isTableBusy.value = true
    store
      .dispatch('app-management/fetchAllGroup', {
        q: searchQuery.value,
        perPage: perPage.value,
        page: currentPage.value,
        sortBy: sortBy.value,
        sortDesc: isSortDirDesc.value,
        customerId: customerId.value,
        includeSubcustomers: includeSubcustomers.value,
      })
      .then(response => {
        const group = response.data.data;
        const total = response.data.length;
        callback(group)
        totalGroup.value = response.data.totalItems
      })
      .catch(() => {
        toast({
          component: ToastificationContent,
          props: {
            title: i18n.t('Error fetching group list'),
            icon: 'AlertTriangleIcon',
            variant: 'danger',
          },
        })
      })
      .finally(() => {
        isTableBusy.value = false
      })

  }

  // *===============================================---*
  // *--------- UI ---------------------------------------*
  // *===============================================---*


  return {
    fetchAllGroup,
    tableColumns,
    perPage,
    currentPage,
    totalGroup,
    dataMeta,
    perPageOptions,
    searchQuery,
    customerId,
    searchCustomer,
    includeSubcustomers,
    hasActiveFilters,
    activeFiltersCount,
    hasAdvancedFilters,
    activeAdvancedFiltersCount,
    clearAllFilters,
    sortBy,
    isSortDirDesc,
    refGroupListTable,
    refetchData,
    isTableBusy,
  }
}
